import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useModuleSettings } from '../../context/adminSettingsContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { hasPermission } from '../../common/permissions';
import { allTutors, approveRejectTutors, changeTutorStatus, deleteTutors } from '../../slice/tutorSlice';
import Swal from 'sweetalert2';
import moment from 'moment';
import DataTable from 'react-data-table-component';

const TutorList = () => {

    const dispatch = useDispatch();
    const { tutorList } = useSelector(state => state.tutor);
    const navigate = useNavigate();
    const location = useLocation();
    const { status } = location.state || {};
    const {
        page,
        setPage,
        limit,
        setLimit,
    } = useModuleSettings()

    const [statusFilter, setStatusFilter] = useState(status || 'active')
    const [searchQuery, setSearchQuery] = useState('')
    const [searchDebounce, setSearchDebounce] = useState('')

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounce(searchQuery)
            setPage(1)
        }, 500)

        return () => clearTimeout(timer)
    }, [searchQuery])

    useEffect(() => {
        dispatch(allTutors({
            page,
            limit,
            filter: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchDebounce || undefined
        }))
    }, [dispatch, page, limit, statusFilter, searchDebounce])

    const handleToggleStatus = async (tutorId) => {
        try {
            const response = await dispatch(changeTutorStatus({
                id: tutorId
            }))
            if (response?.payload?.code == 200) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    backdrop: false,
                    title: response?.payload?.message,
                    showConfirmButton: false,
                    timer: 1500,
                })
            }
            dispatch(allTutors({
                page,
                limit,
                filter: statusFilter !== 'all' ? statusFilter : undefined,
                search: searchDebounce || undefined
            }))
        } catch (error) {
            console.error('Error toggling parent filter:', error)
        }
    }

    // Delete handler
    const handleDelete = async (tutorId) => {
        const result = await Swal.fire({
            title: 'Delete Parent?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        })

        if (!result.isConfirmed) return

        try {
            const deleteResponse = await dispatch(deleteTutors({
                id: tutorId
            }))

            if (deleteResponse?.payload?.code == 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted',
                    text: deleteResponse?.payload?.message,
                    timer: 1500,
                    showConfirmButton: false,
                })
            }

            dispatch(
                allTutors({
                    page,
                    limit,
                    filter: statusFilter !== 'all' ? statusFilter : undefined,
                    search: searchDebounce || undefined
                })
            )
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: error || 'Failed to delete parent.',
            })
        }
    }

    const handleView = (tutorId) => {
        navigate('/tutors/view', {
            state: { tutorId }
        })
    }

    const handleEdit = (tutorId) => {
        navigate('/tutors/edit', {
            state: { tutorId }
        })
    }

    const handleApproveReject = async (tutorId, status) => {
        const isApprove = status === 'approved';

        const result = await Swal.fire({
            title: isApprove ? 'Approve Tutor?' : 'Reject Tutor?',
            text: isApprove
                ? 'This tutor will be approved and granted access.'
                : 'This tutor will be rejected.',
            icon: isApprove ? 'question' : 'warning',
            showCancelButton: true,
            confirmButtonColor: isApprove ? '#28a745' : '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: isApprove ? 'Yes, Approve' : 'Yes, Reject',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        });

        if (!result.isConfirmed) return;

        try {
            const response = await dispatch(
                approveRejectTutors({ id: tutorId, profile_status: status })
            );

            if (response?.payload?.code === 200) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    backdrop: false,
                    title: response?.payload?.message,
                    showConfirmButton: false,
                    timer: 1500,
                });

                dispatch(allTutors({
                    page,
                    limit,
                    filter: statusFilter !== 'all' ? statusFilter : undefined,
                    search: searchDebounce || undefined,
                }));
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: error || `Failed to ${isApprove ? 'approve' : 'reject'} tutor.`,
            });
        }
    };

    const handleApprove = (tutorId) => handleApproveReject(tutorId, 'approved');
    const handleReject = (tutorId) => handleApproveReject(tutorId, 'rejected');

    const columns = [
        {
            name: 'Sr No',
            width: '80px',
            selector: (_, index) =>
                ((tutorList?.paging?.page_no || 1) - 1) * limit + index + 1,
        },
        {
            name: 'User ID',
            selector: row => row.id,
            sortable: true,
            width: '100px',
        },
        {
            name: 'Full Name',
            selector: row =>
                `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'NA',
            sortable: true,
            minWidth: '180px',
        },
        {
            name: 'Email',
            selector: row => row.email || 'NA',
            sortable: true,
            minWidth: '220px',
        },
        {
            name: 'Phone',
            selector: row =>
                row.country_code && row.phone_number
                    ? `${row.country_code}${row.phone_number}`
                    : 'NA',
            minWidth: '150px',
        },
        {
            name: 'DOB',
            selector: row =>
                row.dob ? moment(row.dob).format('DD MMM YYYY') : 'NA',
            sortable: true,
            width: '130px',
        },
        {
            name: 'Joining Date',
            selector: row =>
                row.createdAt
                    ? moment(row.createdAt).local().format('DD MMM YYYY, hh:mm A')
                    : 'NA',
            sortable: true,
            minWidth: '200px',
        },
        {
            name: 'Profile',
            width: '100px',
            center: true,
            cell: row => (
                <img
                    src={row.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                    alt="profile"
                    onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png';
                    }}
                    style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                    }}
                />
            ),
        },
        {
            name: 'Status',
            width: '100px',
            center: true,
            cell: row => (
                <div className="form-check form-switch">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`switch-${row.id}`}
                        checked={row.is_active === 1}
                        onChange={() => handleToggleStatus(row.id)}
                        disabled={!hasPermission('tutors', 'edit') || row.is_deleted === 1}
                        style={{
                            cursor: hasPermission('tutors', 'edit') && row.is_deleted !== 1 ? 'pointer' : 'not-allowed',
                            opacity: row.is_deleted === 1 ? 0.5 : 1
                        }}
                    />
                </div>
            ),
        },
        {
            name: 'Action',
            width: '120px',
            center: true,
            cell: row => (
                <div className="d-flex gap-2 justify-content-center">
                    {hasPermission('tutors', 'view') && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="View"
                            onClick={() => handleView(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px'
                            }}
                        >
                            <i className="fe-eye" style={{ fontSize: '14px' }} />
                        </button>
                    )}

                    {hasPermission('tutors', 'edit') && (
                        <button
                            className="btn btn-sm btn-outline-warning"
                            title="Edit"
                            onClick={() => handleEdit(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px'
                            }}
                        >
                            <i className="fe-edit" style={{ fontSize: '14px' }} />
                        </button>
                    )}

                    {hasPermission('tutors', 'delete') && row.is_deleted !== 1 && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete"
                            onClick={() => handleDelete(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dc3545',
                                borderRadius: '4px'
                            }}
                        >
                            <i className="fe-trash-2" style={{ fontSize: '14px' }} />
                        </button>
                    )}
                </div>
            ),
        },
        {
            name: 'Approve / Reject',
            width: '140px',
            center: true,
            cell: row => {
                const canEdit = hasPermission('tutors', 'edit');

                // 🔒 Final state → show status instead of buttons
                if (row.profile_status === 'approved' || row.profile_status === 'rejected') {
                    return (
                        <span
                            className={`badge ${row.profile_status === 'approved'
                                ? 'bg-success'
                                : 'bg-danger'
                                }`}
                            style={{ padding: '6px 10px', fontSize: '12px' }}
                        >
                            {row.profile_status.toUpperCase()}
                        </span>
                    );
                }

                // ⏳ Pending → show buttons
                return (
                    <div className="d-flex gap-2 justify-content-center">
                        {/* Approve */}
                        <button
                            className="btn btn-sm btn-outline-success"
                            title="Approve"
                            disabled={!canEdit}
                            onClick={() => handleApprove(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: canEdit ? 'pointer' : 'not-allowed',
                                opacity: canEdit ? 1 : 0.5,
                            }}
                        >
                            <i className="fe-check" style={{ fontSize: '14px' }} />
                        </button>

                        {/* Reject */}
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Reject"
                            disabled={!canEdit}
                            onClick={() => handleReject(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: canEdit ? 'pointer' : 'not-allowed',
                                opacity: canEdit ? 1 : 0.5,
                            }}
                        >
                            <i className="fe-x" style={{ fontSize: '14px' }} />
                        </button>
                    </div>
                );
            }
        }

    ]

    const customStyles = {
        table: {
            style: {
                backgroundColor: '#ffffff',
                borderRadius: '8px',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                minHeight: '48px',
            },
        },
        headCells: {
            style: {
                fontSize: '13px',
                fontWeight: '600',
                color: '#495057',
                paddingLeft: '12px',
                paddingRight: '12px',
            },
        },
        cells: {
            style: {
                fontSize: '13px',
                color: '#212529',
                paddingTop: '12px',
                paddingBottom: '12px',
                paddingLeft: '12px',
                paddingRight: '12px',
            },
        },
        rows: {
            style: {
                minHeight: '60px',
                '&:hover': {
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer',
                },
            },
            stripedStyle: {
                backgroundColor: '#ffffff',
            },
        },
        pagination: {
            style: {
                borderTop: '1px solid #dee2e6',
                minHeight: '56px',
                fontSize: '13px',
            },
        },
    }

    return (
        <div className="container-fluid px-4 py-3">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 fw-semibold">Tutors</h4>

                {hasPermission('tutors', 'add') && (
                    <Link
                        to="/tutors/add"
                        className="btn btn-success"
                        style={{
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: '500',
                            borderRadius: '6px'
                        }}
                    >
                        + Add Tutor
                    </Link>
                )}
            </div>

            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-3">
                    <div className="row g-3">
                        <div className="col-md-8">
                            <div className="position-relative">
                                <i
                                    className="fe-search position-absolute"
                                    style={{
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#6c757d',
                                        fontSize: '16px'
                                    }}
                                />
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name, email, username, or phone..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{
                                        paddingLeft: '38px',
                                        paddingRight: searchQuery ? '38px' : '12px',
                                        height: '40px',
                                        fontSize: '14px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px'
                                    }}
                                />
                                {searchQuery && (
                                    <button
                                        className="btn btn-link position-absolute p-0"
                                        type="button"
                                        onClick={() => setSearchQuery('')}
                                        style={{
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: '#6c757d'
                                        }}
                                    >
                                        <i className="fe-x" style={{ fontSize: '16px' }}></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value)
                                    setPage(1)
                                }}
                                style={{
                                    height: '40px',
                                    fontSize: '14px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px'
                                }}
                            >
                                <option value="all">All</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                                <option value="deleted">Deleted</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                <DataTable
                    columns={columns}
                    data={tutorList?.data || []}
                    pagination
                    paginationServer
                    paginationTotalRows={tutorList?.paging?.total || 0}
                    paginationPerPage={limit}
                    paginationDefaultPage={tutorList?.paging?.page_no || 1}
                    onChangeRowsPerPage={(newLimit) => {
                        setLimit(newLimit)
                        setPage(1)
                    }}
                    onChangePage={(newPage) => setPage(newPage)}
                    responsive
                    highlightOnHover
                    persistTableHead
                    progressPending={tutorList?.loading}
                    noDataComponent={
                        <div className="p-5 text-center">
                            <i className="fe-inbox" style={{ fontSize: '48px', color: '#adb5bd' }}></i>
                            <h6 className="mt-3 mb-1 text-muted">
                                {searchQuery ? 'No tutors found matching your search' : 'No tutors found'}
                            </h6>
                            <p className="text-muted small mb-0">
                                {searchQuery ? 'Try adjusting your search criteria' : 'Get started by adding a new parent'}
                            </p>
                        </div>
                    }
                    customStyles={customStyles}
                />
            </div>
        </div>
    )
}

export default TutorList;